import React, { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import Text from "@components/Text";
import Spinner from "@components/Spinner";
import { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";
import {
  AdminDeviceDetail,
  AdminDeviceRequest,
  getDeviceRequests,
  importLegacyTracks,
} from "@services/adminService";
import { ROUTES } from "@stores/AccountStore";

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  font-weight: bold;
  color: #899093;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
`;

const InfoCard = styled.div`
  background: #fafafa;
  border: 1px solid #ebeeef;
  border-radius: 8px;
  padding: 20px 24px;
  margin-bottom: 16px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 16px;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px 32px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const InfoField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const InfoLabel = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-size: 11px;
  color: #899093;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InfoValue = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-size: 14px;
  color: #181b1f;
  word-break: break-all;
`;

const CopyBtn = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  color: #4a90d9;
  cursor: pointer;
  user-select: none;
  &:hover {
    text-decoration: underline;
  }
`;

const UserLink = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-size: 14px;
  color: #4a90d9;
  cursor: pointer;
  word-break: break-all;
  &:hover {
    text-decoration: underline;
  }
`;

const Table = styled.div`
  width: 100%;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 60px 80px 1fr 1fr 60px;
  padding: 10px 16px;
  border-bottom: 1px solid #ebeeef;
  gap: 8px;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 60px 80px 1fr 1fr 60px;
  padding: 10px 16px;
  border-bottom: 1px solid #f5f5f5;
  align-items: center;
  gap: 8px;
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0;
`;

/**
 * Rows per import request. A legacy dump line is ~120 bytes, so this keeps each
 * POST around 2.5 MB — comfortably inside the 10 MB admin body limit.
 */
const LEGACY_BATCH_LINES = 20000;

const DEVICE_TYPES: Record<number, string> = {
  1: "RETROFIT",
  2: "CAN",
  3: "POTATO",
};


interface Props {
  detail: AdminDeviceDetail;
  /** Called after a successful legacy track import so the parent can refetch device detail. */
  onRefreshDetail?: () => void;
}

const DeviceInfoSubTab: React.FC<Props> = ({ detail, onRefreshDetail }) => {
  const navigate = useNavigate();
  const { device, status, attachments } = detail;
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const [deviceRequests, setDeviceRequests] = useState<AdminDeviceRequest[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [legacyLoading, setLegacyLoading] = useState(false);
  const [legacyProgress, setLegacyProgress] = useState<string | null>(null);
  const [legacyMessage, setLegacyMessage] = useState<string | null>(null);
  const [legacyFrom, setLegacyFrom] = useState("");
  const [legacyTo, setLegacyTo] = useState("");
  const [legacyDryRun, setLegacyDryRun] = useState(true);
  const legacyFileInput = useRef<HTMLInputElement>(null);

  const currentAttachment = attachments.find((a) => !a.detachedAt);

  useEffect(() => {
    setRequestsLoading(true);
    getDeviceRequests(device.esn)
      .then((data) => setDeviceRequests(data.requests))
      .catch((e) => console.error("Failed to load requests:", e))
      .finally(() => setRequestsLoading(false));
  }, [device.esn]);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopyFeedback(label);
      setTimeout(() => setCopyFeedback(null), 1500);
    });
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString();
  };

  /**
   * Import legacy `stats-YYYY-MM-DD.csv` dumps from the old backend.
   *
   * The files are whole-fleet, so they are streamed and filtered to this ESN in
   * the browser rather than uploaded whole — a day's dump is far past the 10 MB
   * admin body limit. The filter here is deliberately loose (any line carrying
   * the ESN as a field); the server re-checks the parsed `device_id` column and
   * drops anything else.
   */
  const importLegacyFiles = async (files: FileList) => {
    setLegacyLoading(true);
    setLegacyMessage(null);
    setLegacyProgress(null);

    const needle = `,${device.esn},`;
    const from = legacyFrom
      ? new Date(`${legacyFrom}T00:00:00.000Z`).toISOString()
      : undefined;
    const to = legacyTo
      ? new Date(`${legacyTo}T23:59:59.999Z`).toISOString()
      : undefined;

    const totals = {
      matched: 0,
      candidate: 0,
      inserted: 0,
      alreadyPresent: 0,
      zeroGps: 0,
      outOfRange: 0,
    };
    // Held on a const object rather than as two `let`s: `flush` below is
    // declared inside the per-file loop, and closing over reassigned outer
    // bindings trips eslint's no-loop-func (a build error under CI).
    const span: { min: string | null; max: string | null } = {
      min: null,
      max: null,
    };

    try {
      for (let fi = 0; fi < files.length; fi++) {
        const file = files[fi];
        let header: string | null = null;
        let batch: string[] = [];
        let buffer = "";

        const flush = async () => {
          if (!header || batch.length === 0) return;
          totals.matched += batch.length;
          const res = await importLegacyTracks(device.esn, {
            csv: `${header}\n${batch.join("\n")}`,
            from,
            to,
            dryRun: legacyDryRun,
          });
          batch = [];
          totals.candidate += res.candidateRows;
          totals.inserted += res.inserted;
          totals.alreadyPresent += res.skippedAlreadyPresent;
          totals.zeroGps += res.parse.skippedZeroGps;
          totals.outOfRange += res.parse.skippedOutOfRange;
          if (res.deviceTimeMin && (!span.min || res.deviceTimeMin < span.min)) {
            span.min = res.deviceTimeMin;
          }
          if (res.deviceTimeMax && (!span.max || res.deviceTimeMax > span.max)) {
            span.max = res.deviceTimeMax;
          }
          setLegacyProgress(
            `${file.name} (${fi + 1}/${files.length}) — ${totals.matched} rows read, ` +
              `${legacyDryRun ? "would insert" : "inserted"} ${totals.inserted}`
          );
        };

        const reader = file
          .stream()
          .pipeThrough(new TextDecoderStream())
          .getReader();

        for (;;) {
          const { done, value } = await reader.read();
          if (value) buffer += value;

          let nl = buffer.indexOf("\n");
          while (nl >= 0) {
            const line = buffer.slice(0, nl).replace(/\r$/, "");
            buffer = buffer.slice(nl + 1);
            if (header === null) {
              header = line;
            } else if (line.includes(needle)) {
              batch.push(line);
              if (batch.length >= LEGACY_BATCH_LINES) await flush();
            }
            nl = buffer.indexOf("\n");
          }
          if (done) break;
        }

        const tail = buffer.replace(/\r$/, "");
        if (tail.trim() !== "") {
          if (header === null) header = tail;
          else if (tail.includes(needle)) batch.push(tail);
        }
        await flush();
      }

      const spanText =
        span.min && span.max
          ? ` Device time span: ${span.min} → ${span.max}.`
          : "";
      setLegacyMessage(
        `${legacyDryRun ? "Dry run — nothing written. " : ""}` +
          `${totals.matched} matching rows across ${files.length} file(s); ` +
          `${totals.candidate} valid; ` +
          `${legacyDryRun ? "would insert" : "inserted"} ${totals.inserted}; ` +
          `${totals.alreadyPresent} already present; ` +
          `${totals.zeroGps} dropped for (0,0) GPS; ` +
          `${totals.outOfRange} outside the date range.${spanText}`
      );
      if (!legacyDryRun) onRefreshDetail?.();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Import failed";
      setLegacyMessage(`Import failed: ${msg}`);
    } finally {
      setLegacyLoading(false);
      setLegacyProgress(null);
      if (legacyFileInput.current) legacyFileInput.current.value = "";
    }
  };

  const timeSince = (iso: string) => {
    const diff = Date.now() - new Date(iso).getTime();
    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) return "a few seconds ago";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? "s" : ""} ago`;
  };

  const CopyableField = ({
    label,
    value,
    id,
  }: {
    label: string;
    value: string | null;
    id: string;
  }) => (
    <InfoField>
      <InfoLabel>{label}</InfoLabel>
      <InfoValueRow>
        <InfoValue>{value || "-"}</InfoValue>
        {value && (
          <CopyBtn onClick={() => copyToClipboard(value, id)}>
            {copyFeedback === id ? "Copied!" : "Copy"}
          </CopyBtn>
        )}
      </InfoValueRow>
    </InfoField>
  );

  /** Same shape as CopyableField, but the value is a clickable link that
   *  navigates to the linked entity's admin page. Copy still works alongside.
   *  Used for the User ID quick-link in the device info block. */
  const CopyableLinkField = ({
    label,
    value,
    id,
    onLinkClick,
  }: {
    label: string;
    value: string | null;
    id: string;
    onLinkClick: () => void;
  }) => (
    <InfoField>
      <InfoLabel>{label}</InfoLabel>
      <InfoValueRow>
        {value ? (
          <UserLink title="Open user page" onClick={onLinkClick}>
            {value}
          </UserLink>
        ) : (
          <InfoValue>-</InfoValue>
        )}
        {value && (
          <CopyBtn onClick={() => copyToClipboard(value, id)}>
            {copyFeedback === id ? "Copied!" : "Copy"}
          </CopyBtn>
        )}
      </InfoValueRow>
    </InfoField>
  );

  return (
    <div>
      {/* Basic Parameters */}
      <InfoCard>
        <InfoGrid>
          <CopyableField label="EMI" value={device.esn} id="esn" />
          <InfoField>
            <InfoLabel>Created at</InfoLabel>
            <InfoValue>{formatDate(device.createdAt)}</InfoValue>
          </InfoField>
          <Section style={{ margin: 0 }}>
            <SectionTitle>INTERNAL</SectionTitle>
            <InfoField>
              <InfoLabel>Name</InfoLabel>
              <InfoValue>{device.alias || "-"}</InfoValue>
            </InfoField>
          </Section>

          <CopyableField label="ICCID" value={device.iccid} id="iccid" />
          <InfoField>
            <InfoLabel>Last seen</InfoLabel>
            <InfoValue>
              {status
                ? `${timeSince(status.lastConnectedAt)} (${formatDate(status.lastConnectedAt)})`
                : "-"}
            </InfoValue>
          </InfoField>
          <InfoField>
            <InfoLabel>Description</InfoLabel>
            <InfoValue>{device.description || "-"}</InfoValue>
          </InfoField>

          <CopyableField label="IMEI" value={device.imei} id="imei" />
          <div />
          <InfoField>
            <InfoLabel>Model</InfoLabel>
            <InfoValue>{device.model || "-"}</InfoValue>
          </InfoField>

          {currentAttachment && (
            <>
              <CopyableLinkField
                label="User ID"
                value={currentAttachment.userId}
                id="userId"
                onLinkClick={() =>
                  navigate(`${ROUTES.ADMIN}/users/${currentAttachment.userId}`)
                }
              />
              <div />
              <div />
            </>
          )}
        </InfoGrid>

        <div style={{ marginTop: 16 }}>
          <InfoGrid>
            <div />
            <div />
            <InfoField>
              <InfoLabel>Type</InfoLabel>
              <InfoValue>
                {DEVICE_TYPES[device.deviceType] || String(device.deviceType)}
              </InfoValue>
            </InfoField>
          </InfoGrid>
        </div>

        <div style={{ marginTop: 8 }}>
          <InfoGrid>
            <div />
            <div />
            <InfoField>
              <InfoLabel>Sensor range</InfoLabel>
              <InfoValue>
                {device.sensorMin ?? 0} - {device.sensorMax ?? 2000}
              </InfoValue>
            </InfoField>
          </InfoGrid>
        </div>
      </InfoCard>

      {/* Attachment */}
      {currentAttachment && (
        <InfoCard>
          <SectionTitle>ATTACHMENT</SectionTitle>
          <InfoGrid>
            <InfoField>
              <InfoLabel>User ID</InfoLabel>
              <UserLink
                onClick={() =>
                  navigate(
                    `${ROUTES.ADMIN}/users/${currentAttachment.userId}`
                  )
                }
              >
                {currentAttachment.userId}
              </UserLink>
            </InfoField>
            <InfoField>
              <InfoLabel>Name</InfoLabel>
              <InfoValue>{currentAttachment.name || "-"}</InfoValue>
            </InfoField>
            <InfoField>
              <InfoLabel>Description</InfoLabel>
              <InfoValue>{currentAttachment.description || "-"}</InfoValue>
            </InfoField>
          </InfoGrid>
        </InfoCard>
      )}

      {/* John Deere import (devices.source = "johndeere") */}
      {detail.johnDeere && (
        <InfoCard>
          <SectionTitle>JOHN DEERE</SectionTitle>
          <Text size={13} color="#494c5b" style={{ marginBottom: 12, lineHeight: 1.45 }}>
            Imported from Operations Center. Harvest operations are pulled every 3 hours into
            tracks as a CAN device (value/freq = wet kg / {device.defaultCoefficient ?? 100}).
            The default coefficient applies until a weighed-load calibration.
          </Text>
          <InfoGrid>
            <CopyableField label="VIN / serial" value={detail.johnDeere.vin} id="jdVin" />
            <CopyableField label="Principal ID" value={detail.johnDeere.principalId} id="jdPrincipal" />
            <CopyableField label="Equipment ID" value={detail.johnDeere.equipmentId} id="jdEquipment" />
            <CopyableField label="Organization" value={detail.johnDeere.orgId} id="jdOrg" />
            <InfoField>
              <InfoLabel>Last import</InfoLabel>
              <InfoValue>
                {detail.johnDeere.lastImportAt
                  ? `${timeSince(detail.johnDeere.lastImportAt)} (${formatDate(detail.johnDeere.lastImportAt)})`
                  : "-"}
              </InfoValue>
            </InfoField>
            <InfoField>
              <InfoLabel>Default coefficient</InfoLabel>
              <InfoValue>{device.defaultCoefficient ?? "-"}</InfoValue>
            </InfoField>
          </InfoGrid>
          {detail.johnDeere.lastError && (
            <Text size={13} color="#e74c3c" style={{ marginTop: 12, lineHeight: 1.45 }}>
              Last error: {detail.johnDeere.lastError}
            </Text>
          )}
        </InfoCard>
      )}

      {/* Versions */}
      <InfoCard>
        <SectionTitle>VERSIONS</SectionTitle>
        <InfoGrid>
          <CopyableField label="Module" value={device.moduleVersion} id="module" />
          <CopyableField label="Hardware" value={device.hardwareVersion} id="hardware" />
          <CopyableField label="Bootloader" value={device.bootloaderVersion} id="bootloader" />
          <CopyableField label="Strategy" value={device.strategyVersion} id="strategy" />
          <CopyableField label="GSM firmware" value={device.gsmFirmwareVersion} id="gsm" />
          <CopyableField label="GGNS firmware" value={device.ggnsFirmwareVersion} id="ggns" />
          <CopyableField
            label="Accelerometer firmware"
            value={device.accelerometerFirmwareVersion}
            id="accel"
          />
        </InfoGrid>
      </InfoCard>

      {/* Legacy import only applies to GreenGrowth hardware. */}
      {device.source !== "johndeere" && (<>
      <InfoCard>
        <SectionTitle>IMPORT FROM LEGACY BACKEND</SectionTitle>
        <Text size={13} color="#494c5b" style={{ marginBottom: 12, lineHeight: 1.45 }}>
          For a device that was moved here mid-season: this backend only has the points it
          received itself, starting at the switch-over. Load the old backend's
          nightly dumps (<code>stats-YYYY-MM-DD.csv</code>, one per day, from the{" "}
          <code>stat</code> bucket on DigitalOcean Spaces) to fill in what came before. Pick every
          day from the first working day up to today — a machine that ran offline is dumped on
          the day its data <em>arrived</em>, not the day it worked. Files are filtered to this
          device in the browser and uploaded in batches; points already stored are skipped, so
          re-running is safe.
        </Text>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 16,
            marginBottom: 12,
          }}
        >
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <InfoLabel>From</InfoLabel>
            <input
              type="date"
              value={legacyFrom}
              onChange={(e) => setLegacyFrom(e.target.value)}
              disabled={legacyLoading}
            />
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <InfoLabel>To</InfoLabel>
            <input
              type="date"
              value={legacyTo}
              onChange={(e) => setLegacyTo(e.target.value)}
              disabled={legacyLoading}
            />
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={legacyDryRun}
              onChange={(e) => setLegacyDryRun(e.target.checked)}
              disabled={legacyLoading}
            />
            <Text size={13} color="#494c5b">
              Dry run (count only, write nothing)
            </Text>
          </label>
        </div>

        <input
          ref={legacyFileInput}
          type="file"
          accept=".csv,text/csv"
          multiple
          disabled={legacyLoading}
          onChange={(e) => {
            const files = e.target.files;
            if (files && files.length > 0) void importLegacyFiles(files);
          }}
        />

        {legacyLoading && (
          <Text size={13} color="#494c5b" style={{ marginTop: 12, lineHeight: 1.45 }}>
            {legacyProgress ?? "Reading files…"}
          </Text>
        )}
        {legacyMessage && (
          <Text size={13} color="#181b1f" style={{ marginTop: 12, lineHeight: 1.45 }}>
            {legacyMessage}
          </Text>
        )}
      </InfoCard>
      </>)}

      {/* Requests */}
      <Section>
        <SectionTitle>Requests</SectionTitle>
        <Text size={12} color="#899093" style={{ marginBottom: 12 }}>
          List of outgoing and completed requests
        </Text>
        {requestsLoading ? (
          <LoadingWrap>
            <Spinner />
          </LoadingWrap>
        ) : deviceRequests.length === 0 ? (
          <Text size={13} color="#899093">
            No requests
          </Text>
        ) : (
          <Table>
            <TableHeader>
              <Text size={12} color="#899093" type="heading">
                ID
              </Text>
              <Text size={12} color="#899093" type="heading">
                STATUS
              </Text>
              <Text size={12} color="#899093" type="heading">
                DATA
              </Text>
              <Text size={12} color="#899093" type="heading">
                CREATED
              </Text>
              <Text size={12} color="#899093" type="heading">
                ERROR
              </Text>
            </TableHeader>
            {deviceRequests.map((req) => (
              <TableRow key={req.id}>
                <Text size={12} color="#494c5b">
                  {req.id}
                </Text>
                <Text size={12} color="#494c5b">
                  {req.status || "-"}
                </Text>
                <Text
                  size={11}
                  color="#899093"
                  style={{
                    fontFamily: "monospace",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {JSON.stringify(req.data).substring(0, 80)}
                </Text>
                <Text size={12} color="#899093">
                  {formatDate(req.createdAt)}
                </Text>
                <Text size={11} color="#e74c3c">
                  {req.errorType || ""}
                </Text>
              </TableRow>
            ))}
          </Table>
        )}
      </Section>
    </div>
  );
};

export default DeviceInfoSubTab;
